/**
 * Settings UI grouping for workspace modules — product-oriented sections.
 */

import type { WorkspaceModule } from './workspaceModulesService';

export type ModulePageSectionId = 'platform' | 'previews' | 'tools';

export type ModuleSettingsGroupId =
  | 'strategy'
  | 'planning'
  | 'collaboration'
  | 'measurement_delivery'
  | 'navigation_experience'
  | 'platform_access'
  | 'in_development'
  | 'preview_canvases'
  | 'preview_intelligence'
  | 'preview_decisions'
  | 'tools';

export const MODULE_PAGE_SECTIONS: Record<
  ModulePageSectionId,
  { title: string; description: string }
> = {
  platform: {
    title: 'Platform modules',
    description:
      'Major capabilities for this workspace. Disabled modules are hidden from all members regardless of personal feature flags.',
  },
  previews: {
    title: 'UI previews',
    description:
      'Newer and experimental interfaces. Like every module they start on; turn one off to hide it from everyone.',
  },
  tools: {
    title: 'Prioritization tools',
    description:
      'Session and workspace tools. Disable any tool to hide it from the Tools page and session selectors.',
  },
};

export const MODULE_SETTINGS_GROUPS: Record<
  ModuleSettingsGroupId,
  { title: string; description: string; section: ModulePageSectionId }
> = {
  strategy: {
    title: 'Strategy',
    description: 'Strategy chain, kernel framework, and strategy canvas.',
    section: 'platform',
  },
  planning: {
    title: 'Planning',
    description: 'Plans of Record, how each work type is approached, and plan drift detection.',
    section: 'platform',
  },
  collaboration: {
    title: 'Collaboration & catchball',
    description: 'Stakeholder input, commitments, and catchball workflow.',
    section: 'platform',
  },
  measurement_delivery: {
    title: 'Measurement & delivery',
    description: 'Delivery tracking and backlog health.',
    section: 'platform',
  },
  navigation_experience: {
    title: 'Navigation experience',
    description:
      'How the sidebar and home layout adapt for members — not which apps are available.',
    section: 'platform',
  },
  platform_access: {
    title: 'Platform access & design',
    description: 'Extended navigation and the design hub.',
    section: 'platform',
  },
  in_development: {
    title: 'In development',
    description: 'Capabilities with workspace toggles — disable to hide routes and navigation.',
    section: 'platform',
  },
  preview_canvases: {
    title: 'Design canvases',
    description: 'Replace production pages with v2 canvas prototypes.',
    section: 'previews',
  },
  preview_intelligence: {
    title: 'Language & judgment',
    description: 'Lexicon and Judgment Index preview surfaces.',
    section: 'previews',
  },
  preview_decisions: {
    title: 'Decisions',
    description: 'Ways of making decisions that are not ready for general use yet.',
    section: 'previews',
  },
  tools: {
    title: 'Tools',
    description: 'Prioritization session tools — enable or disable any tool for this workspace.',
    section: 'tools',
  },
};

export const MODULE_SETTINGS_GROUP_ORDER: ModuleSettingsGroupId[] = [
  'strategy',
  'collaboration',
  'measurement_delivery',
  'navigation_experience',
  'platform_access',
  'in_development',
  'planning',
  'preview_canvases',
  'preview_intelligence',
  'preview_decisions',
  'tools',
];

const MODULE_KEY_TO_GROUP: Record<string, ModuleSettingsGroupId> = {
  strategy: 'strategy',
  strategy_kernel: 'strategy',
  strategy_canvas: 'strategy',
  v2mom: 'strategy',

  planning: 'planning',
  plan_drift: 'planning',
  execution_recipe: 'planning',

  catchball: 'collaboration',
  stakeholder_pulse: 'collaboration',
  commitment_registry: 'collaboration',
  requests_and_agreements: 'collaboration',
  delegation: 'collaboration',

  delivery_tracking: 'measurement_delivery',
  cost_of_delay: 'measurement_delivery',
  backlog_health: 'measurement_delivery',

  contributor_role_nav: 'navigation_experience',
  story_overlay: 'navigation_experience',

  elevation_platform: 'platform_access',
  extended_navigation: 'platform_access',
  design_hub: 'platform_access',

  tournaments: 'in_development',
  intake_embed: 'in_development',
  evidence: 'in_development',
  org_hierarchy: 'in_development',
  attributes: 'in_development',
  reports: 'in_development',
  decision_rights: 'in_development',

  cycle_designer_v2: 'preview_canvases',
  track_workspace_v2: 'preview_canvases',

  lexicon: 'preview_intelligence',
  judgment_index: 'preview_intelligence',
  judgment_analytics: 'preview_intelligence',
  ambient_runtime: 'preview_intelligence',
  group_decisions: 'preview_decisions',
  decision_brief: 'preview_decisions',
  decision_intelligence: 'preview_decisions',
  bias_audit: 'preview_decisions',
  decision_calibration: 'preview_decisions',
};

/** Preferred display order within a settings group (falls back to name sort). */
const MODULE_GROUP_SORT_KEYS: Partial<Record<ModuleSettingsGroupId, string[]>> = {
  planning: ['planning', 'execution_recipe', 'plan_drift'],
};

export function getModulePageSection(module: WorkspaceModule): ModulePageSectionId {
  if (module.group === 'tools') return 'tools';
  if (module.group === 'preview') return 'previews';
  return 'platform';
}

export function getModuleSettingsGroup(module: WorkspaceModule): ModuleSettingsGroupId {
  if (module.group === 'tools') return 'tools';

  const mapped = MODULE_KEY_TO_GROUP[module.key];
  if (mapped) return mapped;

  if (module.status === 'needs-wiring' || module.status === 'on-roadmap') {
    return 'in_development';
  }

  if (module.group === 'preview') return 'preview_canvases';

  return 'platform_access';
}

export function groupModules(
  modules: WorkspaceModule[],
): Partial<Record<ModuleSettingsGroupId, WorkspaceModule[]>> {
  const grouped: Partial<Record<ModuleSettingsGroupId, WorkspaceModule[]>> = {};
  for (const mod of modules) {
    const groupId = getModuleSettingsGroup(mod);
    if (!grouped[groupId]) grouped[groupId] = [];
    grouped[groupId]!.push(mod);
  }
  for (const groupId of MODULE_SETTINGS_GROUP_ORDER) {
    const sortKeys = MODULE_GROUP_SORT_KEYS[groupId];
    grouped[groupId]?.sort((a, b) => {
      if (sortKeys) {
        const ai = sortKeys.indexOf(a.key);
        const bi = sortKeys.indexOf(b.key);
        if (ai !== -1 || bi !== -1) {
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        }
      }
      return a.name.localeCompare(b.name);
    });
  }
  return grouped;
}

export function groupsForSection(
  section: ModulePageSectionId,
): ModuleSettingsGroupId[] {
  return MODULE_SETTINGS_GROUP_ORDER.filter(
    (id) => MODULE_SETTINGS_GROUPS[id].section === section,
  );
}
